import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { FirestoreError } from 'firebase/firestore';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { useAuth } from '../core/auth/useAuth';
import {
  createManualTimeEntry,
  createTimerTimeEntry,
  updateTimeEntryFields,
  duplicateTimeEntry,
  deleteTimeEntry,
} from '../core/data/kronosMutations';
import TrackerEntryRow from '../features/kronos/TrackerEntryRow';
import ConfirmDeleteDialog from '../features/admin/ConfirmDeleteDialog';
import {
  formatDurationSeconds,
  sumDurationSeconds,
  type TimeEntryFormValues,
} from '../features/kronos/timeEntries';
import {
  useTrackerProjects,
  useMyRecentEntries,
  useMyHistoryWeeks,
  useProjectDesignations,
  useMultiProjectDesignations,
  useAllUsers,
} from '../features/kronos/useKronosData';
import { groupEntries } from '../features/kronos/entryGroupFilter';
import {
  buildDesignationOptions,
  buildRecencyMap,
} from '../features/kronos/designationOptions';
import { buildTaskSuggestions } from '../features/kronos/taskSuggestions';
import KronosHistory from '../features/kronos/KronosHistory';
import { colors } from '../core/theme/theme';
import { useTimerContext } from '../features/kronos/timer/TimerContext';
import {
  computeTimerEntryTimes,
  continueTimerAction,
} from '../features/kronos/timer/timerMachine';
import DailyProgressBar from '../features/kronos/timer/DailyProgressBar';
import UnifiedTrackerForm from '../features/kronos/timer/UnifiedTrackerForm';
import TeammateScopePicker from '../features/kronos/TeammateScopePicker';
import {
  canViewOtherUsersEntries,
  canEditOtherUsersEntries,
} from '../core/auth/entryScopeAccess';
import ActiveTimerCard from '../features/kronos/timer/ActiveTimerCard';
import TimerStopDialog from '../features/kronos/timer/TimerStopDialog';
import TimerRecoveryDialog from '../features/kronos/timer/TimerRecoveryDialog';
import TimerEditPopover from '../features/kronos/timer/TimerEditPopover';

/**
 * Kronos Tracker page (`/kronos/tracker`) — unified timer + manual
 * time entry for the signed-in user.
 *
 * Composition:
 *   - DailyProgressBar    — combined saved + live-timer progress toward 7 h
 *   - UnifiedTrackerForm  — merged timer/manual form with mode toggle
 *   - ActiveTimerCard     — yellow card replacing the form when timer is running
 *   - TimerStopDialog     — description/tags/billable -> save entry
 *   - TimerRecoveryDialog — recover stale timer found on mount
 *   - TimerEditPopover    — change project/designation/auto-stop mid-timer
 *   - "Today" list        — this user's entries whose startTime >= 00:00
 *                           today, each an inline-editable TrackerEntryRow
 *
 * Data flow: all reads go through the `useKronosData` hooks (real-time
 * `onSnapshot` subscriptions) so edits land live without a manual
 * refresh. Writes go through `core/data/kronosMutations.ts`; recap rows
 * commit each field via `handleInlineUpdate`.
 */
export default function TimeTracker() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // Memoised once per mount
  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Monday 00:00 of the current week — the recap shows the whole week so
  // the user can resume a project started earlier this week / yesterday.
  // (getDay(): 0=Sun…6=Sat → shift so Monday is the week start.)
  const startOfWeek = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const dow = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
    d.setDate(d.getDate() - dow);
    return d;
  }, []);

  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useTrackerProjects(user);

  // Timer context
  const timer = useTimerContext();

  // Unified form drives a single designation subscription. When the
  // timer is active, we also keep the timer's project designation
  // subscription alive (it may target a different project).
  const [formProjectId, setFormProjectId] = useState<string>('');
  const formDesignations = useProjectDesignations(formProjectId);

  // Timer-specific designation subscription
  const [timerProjectId, setTimerProjectId] = useState<string>(
    timer.state?.projectId ?? '',
  );
  const timerDesignations = useProjectDesignations(timerProjectId);

  // Timer edit popover anchor
  const [editAnchor, setEditAnchor] = useState<HTMLElement | null>(null);

  // ── Saisir pour un collègue (admin) ──────────────────────────────
  // Le chronomètre reste personnel par nature : quand on regarde le récap
  // d'un collègue, on corrige ses lignes, on ne démarre pas un compteur en
  // son nom. Pour AJOUTER des heures à quelqu'un, la feuille de temps et le
  // calendrier sont les bons écrans.
  const canSeeOthers = canViewOtherUsersEntries(user?.roles);
  const canEditOthers = canEditOtherUsersEntries(user?.roles);
  const scopeUsers = useAllUsers(canSeeOthers);
  const [scopeUserId, setScopeUserId] = useState('');
  const targetUserId = scopeUserId || user?.uid || '';
  const isOtherUser = Boolean(scopeUserId && scopeUserId !== user?.uid);
  const scopeUserName =
    scopeUsers.users.find((u) => u.id === scopeUserId)?.data.displayName ?? '';

  const {
    entries: weekEntries,
    error: entriesError,
  } = useMyRecentEntries(targetUserId, startOfWeek);

  // Initial history page deliberately small: KronosHistory mounts a full
  // inline-editable TrackerEntryRow per entry (~10 MUI components each), so
  // loading 8 weeks (~200 rows) at once froze the main thread. Load ~2 weeks
  // up front; "Show previous weeks" pulls the rest on demand.
  const history = useMyHistoryWeeks(targetUserId, startOfWeek, 2);

  // History is one-shot (not real-time), so a deleted history row would
  // linger. Track ids deleted this session and filter them out. Harmless
  // for week rows (already removed by the real-time feed).
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const historyEntries = useMemo(
    () => history.entries.filter((e) => !removedIds.has(e.id)).map((e) => e.data),
    [history.entries, removedIds],
  );

  // Today's slice of the week — drives the daily progress bar + today total.
  // Bounded to [today 00:00, tomorrow 00:00) so any future-dated entries
  // still within the week don't leak into "today".
  const startOfTodayMs = startOfToday.getTime();
  const startOfTomorrow = useMemo(() => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() + 1);
    return d;
  }, [startOfToday]);
  const startOfTomorrowMs = startOfTomorrow.getTime();
  const todayEntries = useMemo(
    () =>
      weekEntries.filter((e) => {
        const ms = e.data.startTime.toMillis();
        return ms >= startOfTodayMs && ms < startOfTomorrowMs;
      }),
    [weekEntries, startOfTodayMs, startOfTomorrowMs],
  );

  const projectLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) {
      m.set(p.id, `${p.data.code} — ${p.data.name}`);
    }
    return m;
  }, [projects]);

  // Options for the recap rows' inline project picker (memoised once).
  const projectOptions = useMemo(
    () => projects.map((p) => ({ id: p.id, label: projectLabelById.get(p.id) ?? p.id })),
    [projects, projectLabelById],
  );

  // Designation labels for every project touched this week — the recap
  // spans multiple projects, so the single form/timer subscriptions can't
  // resolve them all (would show raw ids). One listener per project.
  const weekProjectIds = useMemo(
    () => Array.from(new Set(weekEntries.map((e) => e.data.projectId))),
    [weekEntries],
  );
  const weekDesignationLabels = useMultiProjectDesignations(weekProjectIds);

  // History spans projects beyond this week — subscribe to their
  // designations too so history rows resolve labels (not raw ids).
  const historyProjectIds = useMemo(
    () => Array.from(new Set(historyEntries.map((e) => e.projectId))),
    [historyEntries],
  );
  const historyDesignationLabels = useMultiProjectDesignations(historyProjectIds);

  // Recent projects derived from this week's entries (for quick picks).
  // Includes the most recent designation used for that project.
  const recentProjects = useMemo(() => {
    const seen = new Map<string, { label: string; designationId: string }>();
    for (const { data } of weekEntries) {
      if (!seen.has(data.projectId)) {
        const label = projectLabelById.get(data.projectId);
        if (label) seen.set(data.projectId, { label, designationId: data.designationId });
      }
      if (seen.size >= 3) break;
    }
    return Array.from(seen.entries()).map(([id, { label, designationId }]) => ({
      id,
      label,
      designationId,
    }));
  }, [weekEntries, projectLabelById]);

  const [deleteTarget, setDeleteTarget] = useState<TimeEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Tags chosen in the timer form at start; pre-fills the stop dialog. Kept on
  // the page (not in the persisted timer state) — on a mid-timer reload they
  // simply reset and the user re-picks at stop.
  // ponytail: page-level stash, move into timer state only if reload-persistence matters
  const [pendingTimerTags, setPendingTimerTags] = useState<string[]>([]);

  // Unioned label map: week recap + form + timer subscriptions.
  const designationLabelsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const [id, name] of weekDesignationLabels) m.set(id, name);
    for (const [id, name] of historyDesignationLabels) m.set(id, name);
    for (const d of formDesignations.designations) m.set(d.id, d.data.name);
    for (const d of timerDesignations.designations) m.set(d.id, d.data.name);
    return m;
  }, [weekDesignationLabels, historyDesignationLabels, formDesignations.designations, timerDesignations.designations]);

  // Resolve timer project + designation labels for display
  const timerProjectLabel = timer.state
    ? projectLabelById.get(timer.state.projectId) ?? timer.state.projectId
    : '';
  const timerDesignationLabel = timer.state
    ? designationLabelsById.get(timer.state.designationId) ??
      timerDesignations.designations.find(
        (d) => d.id === timer.state?.designationId,
      )?.data.name ??
      timer.state.designationId
    : '';

  // Auto-stop label (e.g. "Stops at 17:30")
  const timerAutoStopLabel = timer.state?.autoStopAt
    ? t('kronos.timer.stopsAt', {
        time: new Date(timer.state.autoStopAt).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      })
    : null;

  // Recency map for the form picker: most recent startTime per designation,
  // scoped to the form's selected project (weekEntries/historyEntries are
  // already the current user's). Keyed by raw designation id — matches the
  // raw id buildDesignationOptions looks up. Drives the "recently used
  // first" ordering within the "On this project" group.
  const myEntries = useMemo(
    () => [...weekEntries.map((e) => e.data), ...historyEntries],
    [weekEntries, historyEntries],
  );
  const formRecentDesignationIds = useMemo(
    () => buildRecencyMap(myEntries, formProjectId),
    [myEntries, formProjectId],
  );
  const timerRecentDesignationIds = useMemo(
    () => buildRecencyMap(myEntries, timerProjectId),
    [myEntries, timerProjectId],
  );

  // Form designation options (existing project designations only),
  // recently-used designations floated to the top.
  const formDesignationOptions = useMemo(
    () =>
      buildDesignationOptions(
        formDesignations.designations.map((d) => d.data),
        [],
        {
          sectionLabel: (s) => t(`projects.designations.sections.${s}`),
          existingGroupLabel: t('kronos.tracker.designationsOnProject'),
          recentGroupLabel: t('kronos.tracker.designationsRecent'),
        },
        formRecentDesignationIds,
      ),
    [formDesignations.designations, t, formRecentDesignationIds],
  );

  // Timer edit popover — same two-group picker as the form, scoped to the
  // timer's own project (which may differ from the form's).
  const timerDesignationOptions = useMemo(
    () =>
      buildDesignationOptions(
        timerDesignations.designations.map((d) => d.data),
        [],
        {
          sectionLabel: (s) => t(`projects.designations.sections.${s}`),
          existingGroupLabel: t('kronos.tracker.designationsOnProject'),
          recentGroupLabel: t('kronos.tracker.designationsRecent'),
        },
        timerRecentDesignationIds,
      ),
    [timerDesignations.designations, t, timerRecentDesignationIds],
  );

  // Suggestions derive from BOTH the real-time week and the loaded
  // history (~3 weeks). With a project selected they are scoped to it and
  // limited to designations still valid on it; with none selected
  // `buildTaskSuggestions` switches to global mode and returns the last
  // tasks across every project, so picking one back-fills the project.
  const validFormDesignationIds = useMemo(
    () => new Set(formDesignations.designations.map((d) => d.id)),
    [formDesignations.designations],
  );
  const taskSuggestions = useMemo(
    () =>
      buildTaskSuggestions(
        [...weekEntries.map((e) => e.data), ...historyEntries],
        formProjectId,
        {
          validDesignationIds: validFormDesignationIds,
          designationLabelsById,
          projectLabelsById: projectLabelById,
        },
      ),
    [
      weekEntries,
      historyEntries,
      formProjectId,
      validFormDesignationIds,
      designationLabelsById,
      projectLabelById,
    ],
  );

  /**
   * Resolve an encoded picker option id to a designation doc id.
   */
  const resolveDesignationId = (encodedOptionId: string): string => {
    if (!encodedOptionId.startsWith('existing:')) {
      throw new Error('Invalid designation option');
    }
    return encodedOptionId.slice('existing:'.length);
  };

  // ── Manual create handler ──────────────────────────────────────────
  const handleCreate = async (payload: {
    values: TimeEntryFormValues;
    times: { startTime: Date; endTime: Date; durationSeconds: number };
  }) => {
    if (!user) return;
    setSaveError(null);
    try {
      const designationId = resolveDesignationId(payload.values.designationId);
      await createManualTimeEntry({
        userId: targetUserId,
        projectId: payload.values.projectId,
        designationId,
        description: payload.values.description,
        tags: payload.values.tags,
        startTime: payload.times.startTime,
        endTime: payload.times.endTime,
        durationSeconds: payload.times.durationSeconds,
        billable: payload.values.billable,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('kronos.tracker.submitFailed');
      setSaveError(message);
      throw err;
    }
  };

  // ── Delete handler ─────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const deletedId = deleteTarget.id;
      await deleteTimeEntry(deletedId);
      setRemovedIds((prev) => new Set(prev).add(deletedId));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  // ── Inline recap edit (per-field update) ────────────────────────────
  const handleInlineUpdate = async (
    entryId: string,
    patch: Record<string, unknown>,
  ) => {
    if (!user) throw new Error('not authenticated');
    await updateTimeEntryFields(entryId, patch);
  };

  // ── Duplicate a recap entry (identical copy) ───────────────────────
  const handleDuplicate = async (entry: TimeEntry) => {
    if (!user || (entry.userId !== user.uid && !canEditOthers)) return;
    setSaveError(null);
    try {
      await duplicateTimeEntry(entry);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('kronos.tracker.submitFailed');
      setSaveError(message);
    }
  };

  // ── Continue timer from an activity ────────────────────────────────
  // `continueTimerAction` decides what the running timer deserves; it used
  // to be saved unconditionally, so pressing ▶ twice on the same row wrote
  // a seconds-long entry that read as a duplicate of that row.
  const handleContinueTimer = async (entry: {
    projectId: string;
    designationId: string;
    description?: string | null;
  }) => {
    if (!user) return;
    const action = continueTimerAction(timer.state, timer.elapsedMs, entry);
    if (action === 'keep') return;
    if (action === 'resume') {
      timer.actions.resume();
      return;
    }
    setSaveError(null);
    try {
      const state = timer.state;
      if (action === 'save' && state) {
        const { startTime, endTime, durationSeconds } = computeTimerEntryTimes(
          state,
          timer.elapsedMs,
        );
        const designationId = state.designationId.startsWith('existing:')
          ? state.designationId.slice('existing:'.length)
          : state.designationId;
        await createTimerTimeEntry({
          userId: user.uid,
          projectId: state.projectId,
          designationId,
          description: (state.description ?? '').trim() || null,
          tags: [],
          startTime,
          endTime,
          durationSeconds,
          billable: true,
        });
      }
      if (action === 'save' || action === 'discard') timer.actions.discard();
      setTimerProjectId(entry.projectId);
      setPendingTimerTags([]);
      timer.actions.start({
        projectId: entry.projectId,
        designationId: entry.designationId,
        autoStopPreset: 'none',
        description: entry.description ?? '',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('kronos.tracker.submitFailed');
      setSaveError(message);
    }
  };

  // ── Timer start handler ────────────────────────────────────────────
  const handleTimerStart = (params: {
    projectId: string;
    designationId: string;
    autoStopPreset: string;
    customAutoStopAt?: string;
    tags: string[];
  }) => {
    // Strip 'existing:' prefix before storing in timer state
    const designationId = params.designationId.startsWith('existing:')
      ? params.designationId.slice('existing:'.length)
      : params.designationId;
    // Also update the timer project subscription
    setTimerProjectId(params.projectId);
    setPendingTimerTags(params.tags);
    timer.actions.start({
      ...params,
      designationId,
      autoStopPreset: params.autoStopPreset as Parameters<typeof timer.actions.start>[0]['autoStopPreset'],
    });
  };

  // ── Timer save handler ─────────────────────────────────────────────
  const handleTimerSave = async (data: {
    description: string;
    tags: string[];
    billable: boolean;
  }) => {
    if (!user || !timer.state) return;
    const state = timer.state;
    const { startTime, endTime, durationSeconds } = computeTimerEntryTimes(
      state,
      timer.elapsedMs,
    );

    // Merge: notes from the timer card + description from the stop dialog
    const timerNotes = (state.description ?? '').trim();
    const dialogNotes = data.description.trim();
    const mergedDescription = [timerNotes, dialogNotes].filter(Boolean).join('\n');

    // Strip 'existing:' prefix if present
    const designationId = state.designationId.startsWith('existing:')
      ? state.designationId.slice('existing:'.length)
      : state.designationId;

    setSaveError(null);
    try {
      await createTimerTimeEntry({
        userId: user.uid,
        projectId: state.projectId,
        designationId,
        description: mergedDescription || null,
        tags: data.tags,
        startTime,
        endTime,
        durationSeconds,
        billable: data.billable,
      });
      timer.actions.discard();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('kronos.tracker.submitFailed');
      setSaveError(message);
      throw err;
    }
  };

  // ── Timer recovery handlers ────────────────────────────────────────
  const handleRecoverySave = () => {
    if (!timer.state) return;
    timer.actions.stop();
    timer.dismissRecovery();
  };

  const handleRecoveryResume = () => {
    timer.dismissRecovery();
  };

  const todayTotalSeconds = sumDurationSeconds(todayEntries.map((e) => e.data));
  const weekTotalSeconds = sumDurationSeconds(weekEntries.map((e) => e.data));
  const error: FirestoreError | null = projectsError ?? entriesError ?? null;

  // Group the week's entries by calendar day (newest day first). Labels
  // resolve to "Today" / "Yesterday" / a localised date in the renderer.
  const dayGroups = useMemo(
    () =>
      groupEntries(
        weekEntries.map((e) => e.data),
        'day',
        { userLabelsById: new Map(), designationLabelsById },
      ),
    [weekEntries, designationLabelsById],
  );

  const dayKeyOf = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const todayKey = dayKeyOf(startOfToday);
  const yesterday = new Date(startOfToday);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = dayKeyOf(yesterday);
  const formatDayLabel = (key: string): string => {
    if (key === todayKey) return t('kronos.entries.todayTitle');
    if (key === yesterdayKey) return t('kronos.entries.yesterday');
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(i18n.language, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const formatWeekLabel = (weekKey: string): string => {
    const [y, m, d] = weekKey.split('-').map(Number);
    const date = new Date(y, m - 1, d).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'long',
    });
    return t('kronos.entries.historyWeekOf', { date });
  };

  // Timer active state for the unified form
  const timerIsActive = Boolean(
    timer.state &&
    (timer.state.status === 'running' || timer.state.status === 'paused') &&
    !timer.needsRecovery,
  );

  return (
    <Box data-testid="time-tracker-page">
      {/* Hero header with background image */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mx: { xs: -2, sm: -3, md: -5 },
          mt: { xs: -2, sm: -3, md: -5 },
          mb: 3,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, sm: 4 },
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src="/images/kronos-header.webp"
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
          }}
        />
        {/* Dark overlay for text readability */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20, 23, 28, 0.55)',
          }}
        />
        {/* Text content */}
        <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ color: colors.white, fontWeight: 700 }}>
            {t('kronos.tracker.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('kronos.tracker.subtitle')}
          </Typography>
        </Stack>
      </Box>

      {/* Daily progress bar */}
      <DailyProgressBar
        totalSeconds={todayTotalSeconds}
        timerElapsedMs={
          timer.state?.status === 'running' ? timer.elapsedMs : 0
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="tracker-error">
          {error.message}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="tracker-save-error">
          {saveError}
        </Alert>
      )}

      {/* When timer is active: show the yellow ActiveTimerCard replacing the form */}
      {/* When idle: show the unified form */}
      {canSeeOthers && (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TeammateScopePicker
            users={scopeUsers.users}
            value={scopeUserId}
            onChange={setScopeUserId}
            placeholder={t('kronos.onBehalf.picker')}
          />
        </Stack>
      )}

      {isOtherUser && (
        <Alert severity={canEditOthers ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {canEditOthers
            ? t('kronos.onBehalf.trackerBanner', { name: scopeUserName })
            : t('kronos.onBehalf.readOnly', { name: scopeUserName })}
        </Alert>
      )}

      {isOtherUser ? null : timerIsActive && timer.state ? (
        <ActiveTimerCard
          status={timer.state.status as 'running' | 'paused'}
          projectLabel={timerProjectLabel}
          designationLabel={timerDesignationLabel}
          description={timer.state.description ?? ''}
          elapsedMs={timer.elapsedMs}
          autoStopLabel={timerAutoStopLabel}
          onPause={timer.actions.pause}
          onResume={timer.actions.resume}
          onStop={timer.actions.stop}
          onEdit={(e) => setEditAnchor(e.currentTarget)}
          onDescriptionChange={timer.actions.updateDescription}
        />
      ) : (
        <UnifiedTrackerForm
          projects={projects.map((p) => ({
            id: p.id,
            code: p.data.code,
            name: p.data.name,
            assigned: p.assigned,
          }))}
          designations={formDesignationOptions}
          projectsLoading={projectsLoading}
          designationsLoading={formDesignations.loading}
          recentProjects={recentProjects}
          taskSuggestions={taskSuggestions}
          timerActive={timerIsActive}
          timerDescription={timer.state?.description ?? ''}
          onProjectIdChange={setFormProjectId}
          onManualSubmit={handleCreate}
          onTimerStart={handleTimerStart}
          onTimerDescriptionChange={timer.actions.updateDescription}
          onContinueRecent={(rp) =>
            handleContinueTimer({
              projectId: rp.id,
              designationId: rp.designationId,
              description: null,
            })
          }
        />
      )}

      {/* This week's entries, grouped by day (newest first) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h3">{t('kronos.entries.weekTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" data-testid="tracker-week-total">
          {formatDurationSeconds(weekTotalSeconds)}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ borderColor: colors.lightGray, borderRadius: 0 }}>
        {weekEntries.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }} data-testid="tracker-week-empty">
            <Typography variant="body2" color="text.secondary">
              {t('kronos.entries.weekEmpty')}
            </Typography>
          </Box>
        ) : (
          dayGroups.map((group) => (
            <Box key={group.key} data-testid={`tracker-day-${group.key}`}>
              {/* Day sub-header: label + day total */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(0,0,0,0.03)',
                  borderTop: `1px solid ${colors.lightGray}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                  {formatDayLabel(group.key)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  data-testid={`tracker-day-total-${group.key}`}
                >
                  {formatDurationSeconds(group.totalSeconds)}
                </Typography>
              </Box>
              {group.entries.map((data) => (
                <TrackerEntryRow
                  key={data.id}
                  entry={data}
                  projectOptions={projectOptions}
                  projectLabel={projectLabelById.get(data.projectId) ?? data.projectId}
                  designationLabel={
                    designationLabelsById.get(data.designationId) ?? data.designationId
                  }
                  onUpdate={(patch) => handleInlineUpdate(data.id, patch)}
                  onDuplicate={() => handleDuplicate(data)}
                  onDelete={() => setDeleteTarget(data)}
                  onContinueTimer={() => handleContinueTimer(data)}
                />
              ))}
            </Box>
          ))
        )}
      </Paper>

      <KronosHistory
        entries={historyEntries}
        projectOptions={projectOptions}
        projectLabelById={projectLabelById}
        designationLabelsById={designationLabelsById}
        hasMore={history.hasMore}
        loading={history.loading}
        loadingMore={history.loadingMore}
        error={Boolean(history.error)}
        onLoadMore={history.loadMore}
        onUpdate={handleInlineUpdate}
        onDuplicate={handleDuplicate}
        onDelete={(entry) => setDeleteTarget(entry)}
        onContinueTimer={(entry) => handleContinueTimer(entry)}
        formatWeekLabel={formatWeekLabel}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        title={t('kronos.entries.deleteTitle')}
        body={t('kronos.entries.deleteBody')}
        busy={busy}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Timer stop dialog */}
      <TimerStopDialog
        open={timer.state?.status === 'stopped' && !timer.needsRecovery}
        projectLabel={timerProjectLabel}
        designationLabel={timerDesignationLabel}
        elapsedFormatted={`${Math.floor(timer.elapsedMs / 3600000)}h${Math.floor((timer.elapsedMs % 3600000) / 60000).toString().padStart(2, '0')}`}
        initialDescription={timer.state?.description ?? ''}
        initialTags={pendingTimerTags}
        onSave={handleTimerSave}
        onDiscard={timer.actions.discard}
        onKeepTiming={() => timer.actions.resume()}
      />

      {/* Timer recovery dialog */}
      {timer.needsRecovery && timer.state && (
        <TimerRecoveryDialog
          open={timer.needsRecovery}
          state={timer.state}
          projectLabel={timerProjectLabel}
          designationLabel={timerDesignationLabel}
          onResume={handleRecoveryResume}
          onSave={handleRecoverySave}
          onDiscard={timer.actions.discard}
        />
      )}

      {/* Timer edit popover */}
      <TimerEditPopover
        anchorEl={editAnchor}
        open={Boolean(editAnchor)}
        onClose={() => setEditAnchor(null)}
        projects={projects.map((p) => ({
          id: p.id,
          code: p.data.code,
          name: p.data.name,
          assigned: p.assigned,
        }))}
        designations={timerDesignationOptions}
        designationsLoading={timerDesignations.loading}
        currentProjectId={timer.state?.projectId ?? ''}
        currentDesignationId={timer.state?.designationId ?? ''}
        currentAutoStopAt={timer.state?.autoStopAt ?? null}
        onProjectIdChange={setTimerProjectId}
        onSave={(params) => {
          const designationId = params.designationId.startsWith('existing:')
            ? params.designationId.slice('existing:'.length)
            : params.designationId;
          timer.actions.updateProject(params.projectId, designationId);
          timer.actions.updateAutoStop(
            params.autoStopPreset,
            params.customAutoStopAt,
          );
        }}
      />
    </Box>
  );
}
